import React, { useMemo, useState } from "react";

import { Alert, Button, EmptyState, SearchField, Section, Segmented } from "../../../Ui/index.jsx";
import { AdminTab, TabLoading } from "./adminShared.jsx";

// Who gained or lost access to what -- team membership and GitHub repository access, merged by the backend
// into one chronological list with one row per person per repository per action. Raw, that is a wall: adding
// one person to a project with six repositories is six near-identical rows. So it is read here the way a person
// would say it -- one sentence per person per action, the repositories tucked in as chips -- grouped by day,
// with anything that FAILED pulled to the top as its own count, because a failed grant is the one row in here
// that someone has to act on.
const ACCESS_WORDS = {
  "Access granted": { verb: "was given access to", subject: "repo" },
  "Invited": { verb: "was invited to", subject: "repo" },
  "Grant failed": { verb: "could NOT be given access to", subject: "repo" },
  "Access revoked": { verb: "lost access to", subject: "repo" },
  "Revoke failed": { verb: "could NOT be removed from", subject: "repo" },
  "Revoke skipped — repository not found": { verb: "needed no change on", subject: "repo" },
  "Stale invite reissued": { verb: "had an expired invite re-sent for", subject: "repo" },
  "Added to team": { verb: "was added to the", subject: "team" },
  "Removed from team": { verb: "was removed from the", subject: "team" },
  "Replaced": { verb: "was replaced on the", subject: "team" },
  "Added back": { verb: "was added back to the", subject: "team" },
  "Status changed": { verb: "had their status changed on the", subject: "team" },
};
const GROUP_WINDOW_MS = 20 * 60 * 1000;
const ACCESS_GROUPS_SHOWN = 40;

function accessDayLabel(date) {
  const d = new Date(date); d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}

// Rows are newest-first. Consecutive rows that are the same person, project, action and actor, close together
// in time, are one thing that happened.
function groupAccessEvents(events) {
  const sorted = [...events].sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const groups = [];
  for (const e of sorted) {
    const last = groups[groups.length - 1];
    const sameThing = last
      && last.kind === e.kind && last.employee === (e.employee_name || "")
      && last.project === (e.project_name || "") && last.event === e.event
      && last.actor === (e.actor_name || "") && last.tone === e.tone
      && Math.abs(new Date(last.latestAt) - new Date(e.at)) <= GROUP_WINDOW_MS;
    if (sameThing) {
      if (e.repository_name && !last.repos.includes(e.repository_name)) last.repos.push(e.repository_name);
      if (e.detail && !last.details.includes(e.detail)) last.details.push(e.detail);
      last.count += 1;
    } else {
      groups.push({
        kind: e.kind, employee: e.employee_name || "", project: e.project_name || "", event: e.event,
        actor: e.actor_name || "", tone: e.tone, latestAt: e.at,
        repos: e.repository_name ? [e.repository_name] : [], details: e.detail ? [e.detail] : [], count: 1,
      });
    }
  }
  return groups;
}

function AccessGroup({ g }) {
  const [open, setOpen] = useState(false);
  const words = ACCESS_WORDS[g.event] || { verb: g.event.toLowerCase(), subject: "repo" };
  const repoCount = g.repos.length;
  const shownRepos = open ? g.repos : g.repos.slice(0, 3);
  const when = new Date(g.latestAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const failed = g.tone === "failed";

  let target;
  if (words.subject === "team") target = <> <b>{g.project || "project"}</b> team</>;
  else if (repoCount === 0) target = <> a repository{g.project ? <> in <b>{g.project}</b></> : null}</>;
  else if (repoCount === 1) target = <> <b>{g.repos[0]}</b>{g.project ? <> <span className="ds-muted">({g.project})</span></> : null}</>;
  else target = <> <b>{repoCount} repositories</b>{g.project ? <> in <b>{g.project}</b></> : null}</>;

  return (
    <Alert tone={failed ? "danger" : undefined} className="Adm-Act">
      <span className={"ds-dot " + (failed ? "ds-dot--danger" : g.tone === "neutral" ? "" : "ds-dot--success")} />
      <div className="Adm-Act__body">
        <p>
          <b>{g.employee || "Someone"}</b> {words.verb}{target}
          <span className="ds-small"> · {g.actor ? `by ${g.actor}` : "automatic"}</span>
        </p>
        {repoCount > 1 && (
          <div className="ds-toolbar">
            {shownRepos.map((r) => <span key={r} className="ds-chip">{r}</span>)}
            {repoCount > 3 && (
              <Button variant="link" size="sm" onClick={() => setOpen((v) => !v)}>{open ? "show less" : `+${repoCount - 3} more`}</Button>
            )}
          </div>
        )}
        {failed && g.details[0] && <p className="ds-small">{g.details[0]}</p>}
        {!failed && g.kind === "team" && g.details[0] && <p className="ds-small">{g.details[0]}</p>}
      </div>
      <span className="ds-small ds-num">{when}</span>
    </Alert>
  );
}

export function AccessActivityTab({ events }) {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(ACCESS_GROUPS_SHOWN);

  const groups = useMemo(() => groupAccessEvents(events || []), [events]);
  const failed = groups.filter((g) => g.tone === "failed");
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups.filter((g) => {
      if (filter === "failed" && g.tone !== "failed") return false;
      if (filter === "github" && g.kind !== "access") return false;
      if (filter === "team" && g.kind !== "team") return false;
      if (!q) return true;
      return [g.employee, g.project, g.actor, ...g.repos].some((x) => String(x || "").toLowerCase().includes(q));
    });
  }, [groups, filter, query]);

  if (!events) return <TabLoading />;
  const days = [];
  for (const g of visible.slice(0, limit)) {
    const label = accessDayLabel(g.latestAt);
    if (!days.length || days[days.length - 1].label !== label) days.push({ label, items: [] });
    days[days.length - 1].items.push(g);
  }
  const FILTERS = [
    { value: "all", label: "Everything" },
    { value: "failed", label: `Needs attention${failed.length ? ` (${failed.length})` : ""}` },
    { value: "github", label: "GitHub access" },
    { value: "team", label: "Team changes" },
  ];

  return (
    <AdminTab title="Access activity" subtitle={`latest ${events.length} changes, grouped`}>
      {!events.length ? <EmptyState>Nothing yet — captured the next time someone's project or repository access changes.</EmptyState> : (
        <>
          {failed.length > 0 ? (
            <Alert tone="danger">
              <Button variant="text" onClick={() => setFilter("failed")}>
                <b>{failed.length}</b> {failed.length === 1 ? "change" : "changes"} did not go through — GitHub refused or errored. Tap to see them.
              </Button>
            </Alert>
          ) : (
            <p className="ds-muted">Nothing failed in this stretch — every grant and removal went through.</p>
          )}
          <div className="ds-toolbar Adm-Tools">
            <Segmented label="Show" options={FILTERS} value={filter} onChange={(key) => { setFilter(key); setLimit(ACCESS_GROUPS_SHOWN); }} />
            <SearchField className="Adm-Search" value={query} onChange={(value) => { setQuery(value); setLimit(ACCESS_GROUPS_SHOWN); }} placeholder="Search a person, project or repository…" />
          </div>
          {!visible.length && <EmptyState>Nothing matches that.</EmptyState>}
          {days.map((day) => (
            <Section key={day.label} title={day.label}>
              <div className="Adm-Stack">
                {day.items.map((g, i) => <AccessGroup key={`${g.latestAt}-${i}`} g={g} />)}
              </div>
            </Section>
          ))}
          {visible.length > limit && (
            <Button onClick={() => setLimit((n) => n + ACCESS_GROUPS_SHOWN)}>
              Show {Math.min(ACCESS_GROUPS_SHOWN, visible.length - limit)} more
            </Button>
          )}
        </>
      )}
    </AdminTab>
  );
}
